// Termin-Mails im Mitteilungszentrum (02.11.2026): Einladungen, Zusagen,
// Absagen und Terminabsagen stehen ganz normal im Posteingang, nur anders
// dargestellt.
//
// Mehr Platz als eine Mail-Zeile: Der Betreff bricht um statt abgeschnitten zu
// werden, darunter Wann · Wo · von wem. Bei einer Einladung drei kleine Knöpfe
// Zusagen / Mit Vorbehalt / Absagen — sie stehen rechts neben dem Text, wenn
// Platz ist, sonst darunter (flex-wrap, keine Breitenweiche). Beantwortet =
// erledigt: Wie in Outlook wandert die Einladung nach dem Antworten in
// „Gelöschte Elemente" (02.11.2026) und fällt heraus. Stand dieselbe Antwort
// schon (etwa in Outlook gegeben), geht nichts raus — dann nur gelesen. Der
// runde Kreis bleibt „als gelesen markieren" wie bei jeder Mail.
//
// Die Einzelheiten (Zeit, Ort, bisherige Antwort) lädt die Zeile selbst — ein
// Graph-Aufruf je SICHTBARER Einladung, wie die Leiste im Lesefenster (T-229).
import * as React from "react";
import {
    CalendarCancel20Regular,
    CalendarCheckmark20Regular,
    CalendarLtr20Regular,
    Checkmark16Regular,
    Dismiss16Regular,
    QuestionCircle16Regular,
} from "@fluentui/react-icons";
import { graphMailService, type EinladungsAntwort, type TerminEinladung } from "../../services/graphMailService";
import { aktiveAntwort, beantwortbar, einladungsFehlerText, mussSenden, terminZeitText } from "../mail/terminEinladung";
import { TERMIN_ART_LABEL, type MeetingMessageType } from "../mail/termineMails";
import { hinweisen } from "../redesign/bestaetigen";
import { NotifAbhaken } from "./NotifAbhaken";
import type { PosteingangMail } from "./clusterNotifications";

const ANTWORTEN: Array<{ a: EinladungsAntwort; label: string; icon: React.ReactElement }> = [
    { a: "zusagen", label: "Zusagen", icon: <Checkmark16Regular /> },
    { a: "vorbehalt", label: "Mit Vorbehalt zusagen", icon: <QuestionCircle16Regular /> },
    { a: "absagen", label: "Absagen", icon: <Dismiss16Regular /> },
];

type Geladen = { einladung: TerminEinladung | null; darfAntworten: boolean };

export const MzTerminZeile: React.FC<{
    m: PosteingangMail;
    touch: boolean;
    /** Zeile öffnen (Mail-Fenster). */
    onOeffnen: () => void;
    /** Kreis: als gelesen markieren (mit Rückmeldung bei Fehler). */
    onGelesen: () => void;
    /** Nichts zu senden (Antwort stand schon): still als gelesen markieren und ausblenden. */
    onErledigt: () => void;
    /** Antwort gesendet: Einladung löschen (Papierkorb) und ausblenden. */
    onBeantwortet: () => void;
}> = ({ m, touch, onOeffnen, onGelesen, onErledigt, onBeantwortet }) => {
    const art = (m.terminArt ?? null) as MeetingMessageType | null;
    const istEinladung = art === "meetingRequest";
    const [daten, setDaten] = React.useState<Geladen | null>(null);
    const [laeuft, setLaeuft] = React.useState<EinladungsAntwort | null>(null);

    React.useEffect(() => {
        if (!istEinladung) return;
        let aktiv = true;
        graphMailService
            .fetchEinladung(m.mailbox, m.id)
            .then((d) => aktiv && setDaten(d))
            // Beiwerk: Ohne Einzelheiten bleibt die Zeile mit Betreff stehen.
            .catch(() => aktiv && setDaten({ einladung: null, darfAntworten: false }));
        return () => {
            aktiv = false;
        };
    }, [istEinladung, m.mailbox, m.id]);

    const e = daten?.einladung ?? null;
    const knoepfe = !!e && !!daten?.darfAntworten && beantwortbar(e);
    const gewaehlt = e ? aktiveAntwort(e.status) : null;

    const antworten = async (ev: React.MouseEvent, a: EinladungsAntwort) => {
        ev.stopPropagation();
        if (!e || laeuft) return;
        // Dieselbe Antwort steht schon (etwa in Outlook gegeben) — nur noch gelesen.
        if (!mussSenden(a, e.status, "")) {
            onErledigt();
            return;
        }
        setLaeuft(a);
        try {
            await graphMailService.beantworteEinladung(m.mailbox, m.id, a, "");
            onBeantwortet();
        } catch (err: unknown) {
            console.error("[MzTerminZeile] Antwort fehlgeschlagen", err);
            await hinweisen({ titel: "Antwort nicht gesendet", text: einladungsFehlerText(err) });
        } finally {
            setLaeuft(null);
        }
    };

    const icon =
        art === "meetingCancelled" || art === "meetingDeclined" ? (
            <CalendarCancel20Regular />
        ) : art === "meetingAccepted" || art === "meetingTentativelyAccepted" ? (
            <CalendarCheckmark20Regular />
        ) : (
            <CalendarLtr20Regular />
        );
    const von = e?.organisator?.name || e?.organisator?.adresse || m.absender;
    const details = e
        ? [terminZeitText(e), e.ort, von ? `von ${von}` : null]
        : [m.absender, !istEinladung ? m.vorschau : null];
    const betreff = e?.betreff || m.betreff;
    const label = art ? TERMIN_ART_LABEL[art] : "Termin";
    const tasten = (ev: React.KeyboardEvent) => {
        if (ev.target !== ev.currentTarget) return;
        if (ev.key === "Enter" || ev.key === " ") {
            ev.preventDefault();
            onOeffnen();
        }
    };

    return (
        <div className="mz-zeile termin" role="button" tabIndex={0} onClick={onOeffnen} onKeyDown={tasten}>
            <span className={`mz-icon${art === "meetingCancelled" ? " ueber" : ""}`} aria-hidden>
                {icon}
            </span>
            <span className="mz-tkoerper">
                <span className="mz-text">
                    <div className="mz-z1">
                        <span className={`mz-tart ${art ?? ""}`}>{label}</span>
                        {betreff}
                    </div>
                    <div className="mz-z2">{details.filter(Boolean).join(" · ")}</div>
                    {e?.abgesagt && <div className="mz-z2 mz-tabgesagt">Dieser Termin wurde abgesagt.</div>}
                </span>
                {knoepfe && (
                    <span className="mz-tantworten" role="group" aria-label="Einladung beantworten">
                        {ANTWORTEN.map(({ a, label: l, icon: i }) => (
                            <button
                                key={a}
                                type="button"
                                className={`mz-tantwort ${a}${gewaehlt === a ? " gewaehlt" : ""}${touch ? " touch" : ""}`}
                                title={gewaehlt === a ? `${l} (bereits gewählt)` : l}
                                aria-label={l}
                                aria-pressed={gewaehlt === a}
                                disabled={laeuft != null}
                                onClick={(ev) => void antworten(ev, a)}
                                onKeyDown={(ev) => ev.stopPropagation()}
                            >
                                {laeuft === a ? <span className="mz-tlaeuft" aria-hidden /> : i}
                            </button>
                        ))}
                    </span>
                )}
            </span>
            <NotifAbhaken touch={touch} label="Als gelesen markieren" onAbhaken={onGelesen} />
        </div>
    );
};
